import React, { useState, useEffect, useMemo } from "react";
import { I18nextProvider } from "react-i18next";
import { FloatingButton } from "./FloatingButton";
import { ChatContainer } from "./ChatContainer";
import { MathCaptcha } from "./MathCaptcha";
import { GPCService } from "@/services/gpcService";
import { useTheme } from "@/hooks/useTheme";
import i18n from "@/i18n";
import {
  WIDGET_POSITIONS,
  DEFAULT_WIDGET_CONFIG,
  BREAKPOINTS,
  API_URL,
  SIZE_DIMENSIONS,
} from "@/utils/constants";
import { isMobile, isCaptchaValid } from "@/utils/helpers";
import {
  WIDGET_VERSION,
  MCP_LOGIN_STORAGE_KEY,
  CAPTCHA_STORAGE_KEYS,
} from "@/config/security";
import { hasEncrypted, removeEncrypted } from "@/services/core/cryptoStorage";
import "@/styles/index.css";
import "@/styles/widget.css";
import "@/styles/responsive.css";
import "@/styles/captcha.css";
export const TiaGPCWidget = ({
  token,
  theme = DEFAULT_WIDGET_CONFIG.theme,
  position = DEFAULT_WIDGET_CONFIG.position,
  language = DEFAULT_WIDGET_CONFIG.language,
  primaryColor,
  size,
  width = DEFAULT_WIDGET_CONFIG.width,
  height = DEFAULT_WIDGET_CONFIG.height,
  apiUrl = API_URL,
  onReady,
  onError,
  onClose,
  autoOpen = DEFAULT_WIDGET_CONFIG.autoOpen,
  showBranding = DEFAULT_WIDGET_CONFIG.showBranding,
  mobileFullscreen = DEFAULT_WIDGET_CONFIG.mobileFullscreen,
  ...props
}) => {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [mcpLoggedIn, setMcpLoggedIn] = useState(
    hasEncrypted(MCP_LOGIN_STORAGE_KEY),
  );
  const [showMcpLoginModal, setShowMcpLoginModal] = useState(false);

  const shouldAutoOpen =
    widgetConfig?.widget_customization?.behavior?.auto_open ?? autoOpen;
  const [isOpen, setIsOpen] = useState(false);

  const gpcService = useMemo(() => {
    return new GPCService(apiUrl, token);
  }, [apiUrl, token]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileDevice(isMobile());
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    if (isCaptchaValid()) {
      setCaptchaVerified(true);
    } else {
      localStorage.removeItem(CAPTCHA_STORAGE_KEYS.VERIFIED);
      localStorage.removeItem(CAPTCHA_STORAGE_KEYS.TIMESTAMP);
    }

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const lang = widgetConfig?.widget_customization?.language || language;
    if (lang && i18n.changeLanguage) {
      i18n.changeLanguage(lang);
    }
  }, [language, widgetConfig]);

  useEffect(() => {
    const loadConfig = async () => {
      if (!token) {
        setConfigLoading(false);
        return;
      }

      try {
        const response = await gpcService.getInitConfig();
        if (response.success) {
          setWidgetConfig(response.data);

          const shouldOpen =
            response.data?.widget_customization?.behavior?.auto_open ??
            autoOpen;
          if (shouldOpen) {
            setIsOpen(true);
          }

          if (onReady) {
            onReady(response.data);
          }
        }
      } catch (error) {
        console.error("[TIA GPC Widget] Error loading config:", error);
        if (onError) {
          onError(error);
        }
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, [token, gpcService, onReady, onError, autoOpen]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!gpcService) return;

      const sessionToken = gpcService.getSessionToken();
      const apiUrl = gpcService.httpClient.baseURL;
      const licenseToken = gpcService.licenseToken;

      fetch(`${apiUrl}/release-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${licenseToken}`,
        },
        body: JSON.stringify({ session_token: sessionToken }),
        keepalive: true,
      }).catch(() => {});
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [gpcService]);

  if (!token) {
    console.error('[TIA GPC Widget] Error: "token" prop is required');
    return null;
  }

  const handleClose = () => {
    setIsOpen(false);
    setShowCaptcha(false);
    setShowMcpLoginModal(false);
    onClose?.();
  };

  const handleOpenWidget = () => {
    if (!captchaVerified) {
      setShowCaptcha(true);
    }
    setIsOpen(true);
  };

  const handleCaptchaSuccess = () => {
    setCaptchaVerified(true);
    setShowCaptcha(false);
    setMcpLoggedIn(hasEncrypted(MCP_LOGIN_STORAGE_KEY));
  };

  const handleMcpSkip = () => {
    setShowCaptcha(false);
  };

  const handleRequestMcpLogin = (action) => {
    if (action === "logout") {
      removeEncrypted(MCP_LOGIN_STORAGE_KEY);
      setMcpLoggedIn(false);
    } else {
      setShowMcpLoginModal(true);
    }
  };

  const handleMcpLoginSuccess = () => {
    setMcpLoggedIn(hasEncrypted(MCP_LOGIN_STORAGE_KEY));
    setShowMcpLoginModal(false);
  };

  const handleMcpLoginClose = () => {
    setShowMcpLoginModal(false);
  };

  const handleCaptchaError = (error) => {
    console.error("[TIA GPC Widget] CAPTCHA error:", error);
    if (onError) {
      onError(error);
    }
  };

  const widgetMobileFullscreen = useMemo(
    () =>
      widgetConfig?.widget_customization?.behavior?.mobile_fullscreen ??
      mobileFullscreen,
    [widgetConfig, mobileFullscreen],
  );

  const positionClass = useMemo(() => {
    const widgetPosition =
      widgetConfig?.widget_customization?.position || position;
    if (isMobileDevice && widgetMobileFullscreen) {
      return "gpc-position-bottom-right";
    }
    return `gpc-position-${widgetPosition}`;
  }, [widgetConfig, isMobileDevice, position, widgetMobileFullscreen]);

  const customStyles = useMemo(() => {
    const styles = {};

    const customPrimaryColor =
      widgetConfig?.widget_customization?.colors?.primary || primaryColor;
    const headerBgColor =
      widgetConfig?.widget_customization?.colors?.header_background;
    const headerTextColor =
      widgetConfig?.widget_customization?.colors?.header_text;

    if (customPrimaryColor) styles["--gpc-color-primary"] = customPrimaryColor;
    if (headerBgColor) styles["--gpc-header-bg"] = headerBgColor;
    if (headerTextColor) styles["--gpc-header-text"] = headerTextColor;

    const configSize =
      widgetConfig?.widget_customization?.dimensions?.size || size;
    const sizePreset = configSize && SIZE_DIMENSIONS[configSize];
    const widgetWidth =
      widgetConfig?.widget_customization?.dimensions?.width ||
      sizePreset?.width ||
      width;
    const widgetHeight =
      widgetConfig?.widget_customization?.dimensions?.height ||
      sizePreset?.height ||
      height;

    if (!isMobileDevice || !widgetMobileFullscreen) {
      if (widgetWidth) styles["--gpc-widget-width"] = widgetWidth;
      if (widgetHeight) styles["--gpc-widget-height"] = widgetHeight;
    }

    return styles;
  }, [
    widgetConfig,
    isMobileDevice,
    size,
    width,
    height,
    primaryColor,
    widgetMobileFullscreen,
  ]);

  const widgetTheme = widgetConfig?.widget_customization?.theme || theme;
  const { theme: resolvedTheme, toggleTheme } = useTheme(widgetTheme);

  return (
    <I18nextProvider i18n={i18n}>
      <div
        className={`gpc-widget gpc-widget-container ${positionClass} ${
          resolvedTheme === "dark" ? "gpc-dark" : ""
        }`}
        style={customStyles}
      >
        {!isOpen && !configLoading && widgetConfig && (
          <FloatingButton
            onClick={handleOpenWidget}
            widgetConfig={widgetConfig}
          />
        )}

        {isOpen && !configLoading && (
          <>
            {showCaptcha && !captchaVerified ? (
              <div className="gpc-chat-window gpc-animate-fade-in">
                <MathCaptcha
                  gpcService={gpcService}
                  mcpConfig={widgetConfig?.mcp}
                  onSuccess={handleCaptchaSuccess}
                  onSkip={handleMcpSkip}
                  onError={handleCaptchaError}
                  onClose={handleClose}
                />
              </div>
            ) : showMcpLoginModal ? (
              <div className="gpc-chat-window gpc-animate-fade-in">
                <MathCaptcha
                  gpcService={gpcService}
                  mcpConfig={widgetConfig?.mcp}
                  onSuccess={handleMcpLoginSuccess}
                  onSkip={handleMcpLoginClose}
                  onError={handleCaptchaError}
                  onClose={handleClose}
                  initialStep="mcp"
                />
              </div>
            ) : (
              <ChatContainer
                gpcService={gpcService}
                licenseToken={token}
                widgetConfig={widgetConfig}
                onClose={handleClose}
                themeMode={resolvedTheme}
                onToggleTheme={toggleTheme}
                showBranding={showBranding}
                showMetadata={props.showMetadata || false}
                onError={onError}
                version={WIDGET_VERSION}
                onRequestMcpLogin={handleRequestMcpLogin}
                mcpLoggedIn={mcpLoggedIn}
              />
            )}
          </>
        )}
      </div>
    </I18nextProvider>
  );
};
